import {
    Directive,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {select, Store} from '@ngrx/store';
import {EulaComponent} from '@shared/components/eula/eula.component';
import {
    EulaAgreementPayload,
    EulaData,
    EulaResponse,
    EulaService,
} from '@shared/services/eula.service';
import {getUser, UserState} from '@core/states/user.state';
import {of, Subscription} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';

@Directive({
    selector: '[oct-eula-agreement]',
})
export class EulaDirective implements OnInit {
    @Input() showEulaOnClick: boolean = true;
    @Output() eulaAccepted = new EventEmitter<boolean>();

    private userState: UserState;

    constructor(
        private dialog: MatDialog,
        private eulaService: EulaService,
        private store: Store<any>,
        private toasterService: ToastrService
    ) {
    }

    ngOnInit(): void {
        this.store.pipe(select(getUser)).subscribe((user: UserState) => {
            this.userState = user;
        });
    }

    @HostListener('click', ['$event']) onClick() {
        if (!this.showEulaOnClick) {
            this.eulaAccepted.emit(true);
            return;
        }

        let eulaData: EulaData;
        this.eulaService
            .getEulaAgreed(+this.userState.id)
            .pipe(
                switchMap((eula: EulaData) => {
                    if (!eula.acceptanceRequired) {
                        this.eulaAccepted.emit(true);
                        return of();
                    }
                    eulaData = eula;
                    const dialogRef = this.dialog.open(EulaComponent, {data: eula});
                    return dialogRef.afterClosed();
                }),
                switchMap((isEulaAccepted: boolean) => {
                    if (!isEulaAccepted) {
                        return of({eulaAccepted: false});
                    }
                    const userAgreementData: EulaAgreementPayload = {
                        userId: +this.userState.id,
                        eula: {
                            hash: eulaData.eula.hash,
                            version: eulaData.eula.version,
                        },
                    };
                    return this.eulaService.setEulaAgreed(userAgreementData);
                })
            )
            .subscribe({
                next: (eulaResponse: EulaResponse) => {
                    if (eulaResponse && eulaResponse.acceptanceId) {
                        this.eulaAccepted.emit(true);
                        this.toasterService.success('EULA accepted!');
                    } else {
                        this.eulaAccepted.emit(false);
                    }
                },
                error: (error) => {
                    switch (error.status) {
                        case 400:
                            this.toasterService.error('Invalid request. Please try again.');
                            this.eulaAccepted.emit(false);
                            break;
                        case 403:
                            this.toasterService.error('User is not admin!');
                            this.eulaAccepted.emit(false);
                            break;
                        case 409:
                            this.toasterService.warning('EULA is already accepted!'); // even it is treated as error we allow the user to continue because eula is already accepted
                            this.eulaAccepted.emit(true);
                            break;
                        default:
                            this.toasterService.error('Something went wrong');
                            console.error('Error saving EULA agreement', error);
                            this.eulaAccepted.emit(false);
                            break;
                    }
                },
            });
    }
}
